import { DateTime } from 'luxon'
import { digObject, formatDate, sortArrayBy } from '@domain-group/javascript-utils'

import { agencyTeamMemberSort } from '@functions/teamMember'

import type { CampaignModel, TeamMemberModel, UserModel } from '@models/types'
import type { EntitiesState } from '@redux/entities'

export const getFullAddress = (campaign: CampaignModel) => {
  const {
    post_code, state_name, street_name, street_number, suburb_name, title, unit_number,
  } = campaign || {}

  const unitWithStreet = unit_number ? `${unit_number}/${street_number}` : street_number
  const street = title || `${unitWithStreet} ${street_name}, ${suburb_name}`

  return `${street}, ${state_name} ${post_code}`
}

type CalculateCampaignDurationParams = {
  startDateString: string,
  endDateString: string,
}

export const calculateCampaignDuration = (params: CalculateCampaignDurationParams) => {
  const { endDateString, startDateString } = params

  const startDateObj = DateTime.fromFormat(startDateString, 'yyyy-MM-dd')
  const endDateObj = DateTime.fromFormat(endDateString, 'yyyy-MM-dd')

  const duration = Math.round(endDateObj.diff(startDateObj, 'weeks').weeks)
  return `${duration}w`
}

export type AgentsAndMembers = {
  member_role: keyof typeof agencyTeamMemberSort,
  user_id: number,
  [key: string]: any,
}

export const combineAgentsAndTeamMembers = (
  teamMembers: TeamMemberModel[],
  agentIds: number[],
  agents: EntitiesState['agents'],
): AgentsAndMembers[] => {
  const agentUserIds = agentIds.map(id => agents[id]?.user_id).filter(id => id)

  // @ts-expect-error TS2322
  const members: AgentsAndMembers[] = [...teamMembers].filter(member => !agentUserIds.includes(member.user_id))

  agentIds?.forEach((id, index) => {
    const member_role = index === 0 ? 'lead_agent' as const : 'associate_agent' as const
    const member = { member_role, user_id: agents[id]?.user_id }

    // @ts-expect-error TS2345
    members.splice(index, 0, member)
  })

  return sortArrayBy(members, 'asc', member => agencyTeamMemberSort[member.member_role])
}

export const mapToState = (campaign: Partial<CampaignModel>) => {
  const auctionDate = digObject(campaign, 'dates.auction.date_time', '')
  const formattedDate = formatDate(auctionDate, 'dd/M/yyyy HH:mm', 'yyyy-MM-dd\'T\'HH:mm')

  return {
    ...campaign,
    auction_date: formattedDate,
  }
}

export const getCampaignType = (campaign: Partial<CampaignModel>) => (campaign?.agency_marketing
  ? 'agency_marketing' : 'property_marketing')

export const getCampaignPermissionPath = (
  currentUser: any,
  permissionGroupKey: string,
  permissionKeyName: string,
  campaignType?: 'property_marketing' | 'agency_marketing' | 'notifications' | undefined,
): boolean => {
  if (currentUser.enable_brand_custom_profiles && campaignType){
    const permissionPath = currentUser.custom_permissions
      ?.agency?.brand_custom_profile?.[campaignType]?.[permissionGroupKey]?.[permissionKeyName]
    return permissionPath !== undefined ? permissionPath : true
  }

  const campaignPermissions = currentUser.custom_permissions?.agency?.campaign

  if (campaignType && campaignPermissions?.[campaignType]){
    const permissionPath = campaignPermissions[campaignType]?.[permissionGroupKey]?.[permissionKeyName]
    return permissionPath !== undefined ? permissionPath : true
  }

  const permissionPath = campaignPermissions?.[permissionGroupKey]?.[permissionKeyName]

  return permissionPath !== undefined ? permissionPath : true
}

type GetCampaignPermissionsParams = {
  user: Partial<UserModel>,
  campaignType?: 'property_marketing' | 'agency_marketing',
}

export const getCampaignPermissions = (
  params: GetCampaignPermissionsParams,
) => {
  const {
    user, campaignType,
  } = params

  if (user?.enable_brand_custom_profiles && campaignType){
    const permissionObject = user?.frontend_permissions
      ?.agency?.brand_custom_profile?.[campaignType]

    return permissionObject !== undefined ? permissionObject : {}
  }

  if (!user?.enable_brand_custom_profiles && user?.enable_custom_permissions){
    const campaignPermissions = user?.frontend_permissions?.agency?.campaign

    if (campaignType && campaignPermissions?.[campaignType]){
      const permissionObject = campaignPermissions?.[campaignType]

      return permissionObject !== undefined ? permissionObject : {}
    }

    return campaignPermissions !== undefined ? campaignPermissions : {}
  }

  return user?.frontend_permissions?.agency?.campaign || {}
}

export default combineAgentsAndTeamMembers
